import { useMemo } from 'react'

import { InsightsDashboardPoolState } from '@aytm/app/insights/dashboard/duckness/ducks/types'
import InsightsDashboardPool from '@aytm/app/insights/dashboard/duckness/InsightsDashboardPool'
import usePool from '@duckness/use-pool'
import type { AttributeRow } from '@aytm/app/insights/dashboard/models/types.ClientDTO'
import type { UseAttributesReturnType } from './types'
import {
  metricsFactory,
  type GetMetrics,
  captionsFactory,
  type GetCaptions
} from '@aytm/app/insights/dashboard/helpers/attributes'

import { stateSelector, shouldUpdate, type StateSelectorParams } from './selectors'

export const useAttributesInternal = (): UseAttributesReturnType => {
  const {
    attributes,
    dataMetrics,
    sections,
    trackTarget,
    sortByAttribute,
    sortByMethod,
    sortOrderPositions,
    chartAttribute,
    chartMethod
  } = usePool<typeof InsightsDashboardPool, StateSelectorParams, InsightsDashboardPoolState>(
    InsightsDashboardPool,
    stateSelector,
    shouldUpdate
  )

  const sortByKey = useMemo(() => attributes?.[sortByAttribute]?.key || null, [attributes, sortByAttribute])

  /** Collect data metrics */
  const getMetrics = useMemo<GetMetrics>(
    () => metricsFactory({ attributes, dataMetrics, sections }),
    [attributes, dataMetrics, sections]
  )

  /** Collect data captions */
  const getCaptions = useMemo<GetCaptions>(
    () => captionsFactory({ attributes, dataMetrics, sections, trackTarget }),
    [attributes, dataMetrics, sections, trackTarget]
  )

  const shownAttributes = useMemo(
    () =>
      getCaptions({
        showSections: false,
        showMetrics: true,
        showMetadata: false,
        showCollapsed: true,
        showUnique: true
      }).map(caption => {
        const cap = caption as AttributeRow
        return 'attribute' === cap.type ? cap.attribute : `${cap.attribute}:${cap.metrics.id}`
      }),
    [getCaptions]
  )

  return useMemo(() => {
    return {
      sections,
      dataMetrics,
      attributes,
      shownAttributes,
      sortByAttribute,
      sortOrderPositions,
      sortByMethod,
      sortByKey,
      chartAttribute,
      chartMethod,
      getMetrics,
      getCaptions
    }
  }, [
    attributes,
    chartAttribute,
    chartMethod,
    dataMetrics,
    getCaptions,
    getMetrics,
    sections,
    shownAttributes,
    sortByAttribute,
    sortOrderPositions,
    sortByKey,
    sortByMethod
  ])
}
